/**
 * Vue Number Format 3.27.0
 * (c) 2021-2023 Dipak Sarkar <hello@dipaksarkar.in> (https://dipaksarkar.in/)
 * @license MIT
 */
import { defineComponent, ref, computed, watch, resolveDirective, withDirectives, openBlock, createElementBlock } from 'vue';

var options$1 = {
    prefix: '',
    suffix: '',
    separator: ',',
    decimal: '.',
    precision: 2,
    minimumFractionDigits: null,
    prefill: true,
    reverseFill: false,
    min: null,
    max: null,
    nullValue: ''
};

function between(min, n, max) {
    return Math.max(min, Math.min(n, max));
}
function fixed(precision) {
    return between(0, precision, 20);
}
class NumberFormat {
    options;
    input;
    number;
    isClean;
    isCustomDecimal;
    preSurRegExp;
    numberRegExp;
    cleanRegExp;
    negativeRegExp;
    constructor(config) {
        this.options = Object.assign(options$1, config);
        const { prefix, suffix, decimal, reverseFill } = this.options;
        this.input = '';
        this.number = '';
        this.isClean = !reverseFill;
        this.preSurRegExp = new RegExp(`${prefix}|${suffix}`, 'g');
        this.numberRegExp = new RegExp(`[^0-9\\${decimal}]+`, 'gi');
        this.cleanRegExp = new RegExp('[^0-9]+', 'gi');
        this.negativeRegExp = new RegExp('[^0-9\\-]+', 'gi');
        this.isCustomDecimal = decimal !== '.';
    }
    isNull() {
        return !this.numberOnly(this.isClean ? this.cleanRegExp : this.negativeRegExp);
    }
    clean(clean = false) {
        this.isClean = clean;
        return this;
    }
    sign() {
        if (this.input === null || this.input === undefined) {
            return '';
        }
        const hasMinus = this.input.toString().indexOf('-') >= 0;
        if (this.isClean) {
            return hasMinus && this.realNumber() > 0 ? '-' : '';
        }
        return hasMinus ? '-' : '';
    }
    toFixed() {
        const exp = Math.pow(10, this.options.precision);
        const float = parseFloat(this.numberOnly(/\D+/g)) / exp || 0;
        return float.toFixed(fixed(this.options.precision));
    }
    toNumber(str) {
        return Number(str);
    }
    numberOnly(regExp) {
        return this.input
            ?.toString()
            .replace(this.preSurRegExp, '')
            .replace(regExp || this.numberRegExp, '');
    }
    isNegative() {
        return this.sign() === '-';
    }
    numbers() {
        const { reverseFill, decimal } = this.options;
        const hasDeciaml = this.input.toString().indexOf(decimal) >= 0;
        const input = !hasDeciaml && this.isCustomDecimal ? this.input + decimal : this.input;
        if (reverseFill) {
            this.number = this.toFixed().replace('.', decimal);
        }
        else if (typeof this.input === 'number') {
            this.number = this.parts(this.input.toString().replace('-', ''), '.').join(decimal);
        }
        else if (!isNaN(this.toNumber(input))) {
            this.number = this.parts(this.input.replace('-', ''), '.').join(decimal);
        }
        else {
            this.number = this.parts(this.numberOnly()).join(decimal);
        }
        return this.number;
    }
    unformatNumber() {
        return this.numbers().toString().replace(this.options.decimal, '.');
    }
    realNumber() {
        return parseFloat(this.unformatNumber());
    }
    parts(num, separator) {
        const { precision, minimumFractionDigits, decimal } = this.options;
        let parts = num.toString().split(separator || decimal);
        if (parts.length > 1) {
            parts[0] = this.toNumber(parts[0]) || 0;
            parts[1] = parts.slice(1, parts.length).join('');
            parts = parts.slice(0, 2);
        }
        if (this.isClean) {
            const newNumber = this.toNumber(parts.join('.')).toFixed(precision);
            const cleanNumber = this.toNumber(newNumber);
            const minimumDigits = cleanNumber.toFixed(minimumFractionDigits);
            const hasMinFraction = minimumFractionDigits >= 0 && cleanNumber.toString().length < minimumDigits.length;
            if (hasMinFraction) {
                parts = minimumDigits.toString().split('.');
            }
            else {
                parts = cleanNumber.toString().split('.');
            }
        }
        return parts.slice(0, 2);
    }
    addSeparator() {
        const { decimal, separator } = this.options;
        const parts = this.numbers().split(decimal);
        parts[0] = parts[0].toString().replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`);
        return parts.join(decimal);
    }
    format(input) {
        this.input = input;
        const { reverseFill, nullValue, prefix, suffix } = this.options;
        if (this.isNull() && !reverseFill) {
            return nullValue;
        }
        return this.sign() + prefix + this.addSeparator() + suffix;
    }
    unformat(input) {
        this.input = input;
        const { reverseFill, nullValue } = this.options;
        if (this.isNull()) {
            return nullValue;
        }
        if (reverseFill && this.realNumber() === 0) {
            return nullValue;
        }
        return this.sign() + this.realNumber();
    }
}

const MINUS = '-';
function cloneDeep(data) {
    return JSON.parse(JSON.stringify(data));
}
function InputEvent(event) {
    return new CustomEvent(event, {
        bubbles: true,
        cancelable: true,
        detail: { facade: true }
    });
}
function getInputElement(el) {
    const inputElement = el instanceof HTMLInputElement ? el : el.querySelector('input');
    if (!inputElement) {
        throw new Error('facade directive requires an input element');
    }
    return inputElement;
}
function updateCursor(el, position) {
    const setSelectionRange = () => {
        el.setSelectionRange(position, position);
    };
    setSelectionRange();
    setTimeout(setSelectionRange, 1);
}
function updateValue(el, vnode, { emit = true, force = false, clean = false } = {}) {
    const { options, oldValue } = el;
    const { reverseFill, max, min } = options;
    const currentValue = vnode?.props?.value || el.value;
    if (force || oldValue !== currentValue) {
        const number = new NumberFormat(options).clean(clean && !reverseFill);
        let masked = number.format(currentValue);
        let unmasked = number.clean(!reverseFill).unformat(currentValue);
        if (clean) {
            if (Number(max) === max && Number(unmasked) > max) {
                masked = number.format(max);
                unmasked = number.unformat(max);
            }
            else if (Number(min) === min && Number(unmasked) < min) {
                masked = number.format(min);
                unmasked = number.unformat(min);
            }
        }
        el.oldValue = masked;
        el.unmasked = unmasked;
        if (el.value !== masked) {
            el.value = masked;
        }
        return emit && el.dispatchEvent(InputEvent('input'));
    }
}
function inputHandler(event) {
    const { target, detail } = event;
    if (detail?.facade) {
        return false;
    }
    event.stopPropagation();
    let positionFromEnd = target.value.length;
    const { oldValue, options } = target;
    if (target.selectionEnd) {
        positionFromEnd = target.value.length - target.selectionEnd;
    }
    updateValue(target, null, { emit: false, clean: !options.precision });
    if (options.suffix) {
        positionFromEnd = Math.max(positionFromEnd, options.suffix.length);
    }
    positionFromEnd = target.value.length - positionFromEnd;
    if (options.prefix) {
        positionFromEnd = Math.max(positionFromEnd, options.prefix.length);
    }
    updateCursor(target, positionFromEnd);
    if (oldValue !== target.value) {
        target.dispatchEvent(InputEvent('input'));
    }
}
function blurHandler(event) {
    const { target, detail } = event;
    if (detail?.facade) {
        return false;
    }
    const { oldValue } = target;
    updateValue(target, null, { force: true, emit: false, clean: true });
    if (oldValue !== target.value) {
        target.dispatchEvent(InputEvent('change'));
    }
}
function keydownHandler(event, el) {
    const { options } = el;
    const { prefix, suffix, decimal, min, separator } = options;
    const { key } = event;
    const regExp = new RegExp(`${prefix}|${suffix}`, 'g');
    const newValue = el.value.replace(regExp, '');
    const canNegativeInput = min === undefined || Number(min) < 0 || Number(min) !== min;
    if (key === decimal) {
        if (newValue.includes(decimal)) {
            event.preventDefault();
        }
        else if (!newValue) {
            el.value = '0' + decimal;
            el.dispatchEvent(new Event('input'));
        }
    }
    else if (key === MINUS && !canNegativeInput) {
        event.preventDefault();
    }
    else if (key === 'Backspace') {
        const selectionEnd = el.selectionEnd || 0;
        const character = el.value.slice(selectionEnd - 1, selectionEnd);
        const replace = el.value.slice(selectionEnd - 2, selectionEnd);
        let positionFromEnd = el.value.length - selectionEnd;
        if ([prefix, MINUS, separator].includes(character)) {
            event.preventDefault();
            if (character === separator) {
                el.value = el.value.replace(replace, '');
            }
            else {
                el.value = el.value.replace(new RegExp(`[${prefix}${MINUS}]`, 'g'), '');
            }
            positionFromEnd = Math.max(positionFromEnd, suffix.length);
            positionFromEnd = el.value.length - positionFromEnd;
            positionFromEnd = Math.max(positionFromEnd, prefix.length);
            updateCursor(el, positionFromEnd);
            el.dispatchEvent(new Event('input'));
        }
    }
}

var directive = {
    beforeMount: (el, { value, modifiers }, vnode) => {
        el = getInputElement(el);
        const options = Object.assign(cloneDeep(options$1), value, modifiers);
        const { reverseFill, precision, decimal } = options;
        el.options = options;
        if (reverseFill && el.value) {
            el.value = parseFloat(new NumberFormat({ ...options, reverseFill: false }).unformat(el.value)).toFixed(precision);
            if (vnode?.props?.value) {
                vnode.props.value = el.value;
            }
        }
        else if (el.value && !isNaN(Number(el.value))) {
            el.value = el.value.replace('.', decimal);
        }
        updateValue(el, vnode, { force: options.prefill, clean: true, emit: false });
    },
    mounted: (el) => {
        el = getInputElement(el);
        const handlerOwner = el.parentElement || el;
        const oninput = (e) => {
            if (e.target !== el) {
                return;
            }
            inputHandler(e);
        };
        handlerOwner.addEventListener('input', oninput, true);
        el.onblur = (e) => blurHandler(e);
        el.onkeydown = (e) => keydownHandler(e, el);
        el.cleanup = () => handlerOwner.removeEventListener('input', oninput, true);
    },
    updated: (el, { value, oldValue, modifiers }, vnode) => {
        el = getInputElement(el);
        const options = el.options;
        el.options = Object.assign(options, value, modifiers);
        if (value !== oldValue) {
            updateValue(el, vnode, { force: true, clean: true });
        }
        else {
            updateValue(el, vnode);
        }
    },
    unmounted: (el) => {
        getInputElement(el).cleanup();
    }
};

const options = cloneDeep(options$1);
var script = defineComponent({
    name: 'VueNumber',
    directives: {
        number: directive
    },
    props: {
        modelValue: {
            type: [String, Number],
            required: true
        },
        nullValue: {
            type: [Number, String],
            required: false,
            default: options.nullValue
        },
        masked: {
            type: Boolean,
            default: false
        },
        reverseFill: {
            type: Boolean,
            default: options.reverseFill
        },
        prefill: {
            type: Boolean,
            default: options.prefill
        },
        precision: {
            type: Number,
            default: () => options.precision
        },
        minimumFractionDigits: {
            type: Number,
            default: () => options.minimumFractionDigits
        },
        decimal: {
            type: String,
            default: () => options.decimal
        },
        min: {
            type: Number,
            default: () => options.min
        },
        max: {
            type: Number,
            default: () => options.max
        },
        separator: {
            type: String,
            default: () => options.separator
        },
        prefix: {
            type: String,
            default: () => options.prefix
        },
        suffix: {
            type: String,
            default: () => options.suffix
        }
    },
    emits: ['update:model-value', 'input:model-value'],
    setup(props, { emit }) {
        const maskedValue = ref(props.modelValue);
        const unmaskedValue = ref('');
        const config = computed(() => ({ ...props }));
        const emittedValue = computed(() => {
            return props.masked ? maskedValue.value : unmaskedValue.value;
        });
        const input = (event) => {
            const { target } = event;
            maskedValue.value = target.value;
            unmaskedValue.value = target.unmasked;
            emit('input:model-value', emittedValue.value);
        };
        const change = () => {
            emit('update:model-value', emittedValue.value);
        };
        watch(() => props.modelValue, (newValue, oldValue) => {
            if (newValue !== oldValue) {
                maskedValue.value = newValue;
            }
        });
        return {
            config,
            maskedValue,
            unmaskedValue,
            input,
            change
        };
    }
});

const _hoisted_1 = ["value"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _directive_number = resolveDirective("number");
  return withDirectives((openBlock(), createElementBlock("input", {
    type: "text",
    autocomplete: "off",
    value: _ctx.maskedValue,
    class: "v-number vue-number-format",
    onChange: _cache[0] || (_cache[0] = (...args) => (_ctx.change && _ctx.change(...args))),
    onInput: _cache[1] || (_cache[1] = (...args) => (_ctx.input && _ctx.input(...args)))
  }, null, 40 , _hoisted_1)), [
    [_directive_number, _ctx.config]
  ])
}

script.render = render;
script.__file = "src/component.vue";

const VueNumberFormat = {
    install(app, config) {
        if (config) {
            Object.assign(options$1, config);
        }
        app.directive('number', directive);
        app.component('VueNumber', script);
    }
};

export { NumberFormat, script as component, VueNumberFormat as default, directive, options$1 as options };
